/**
 * Maps a patient record + provider name to the document-mode field-map data paths
 * (consent-document-builder CHIP_GROUPS) for the fill-and-sign popup's auto-fill display.
 *
 * This MIRRORS the BE `ConsentFillDataResolver` (Scrips.Patient PR #884) so the values the
 * provider sees in the popup match what the server bakes into the PDF. The BE re-resolves
 * authoritatively at sign-time from `GET /api/Patients/{id}`; this is the display copy.
 */
import { todayLocalYmd } from '@/shared/lib/ymd-local';
import type { Patient } from '@/types/patient-api';
import type { ConsentSigner } from '@/consent/types/consent-api';

function joinName(...parts: (string | null | undefined)[]): string {
  return parts.filter((p) => p && p.trim()).map((p) => p!.trim()).join(' ');
}

function defaultAddress(patient: Patient): string {
  const list = patient.updatePatientContactRequest?.patientAddress ?? [];
  const a = list.find((x) => x?.isDefault) ?? list[0];
  if (!a) return '';
  return [a.building, a.apartment, a.address, a.city, a.country]
    .filter((s) => s && String(s).trim())
    .map((s) => String(s).trim())
    .join(', ');
}

/** Value for a field-map `key`, or `''` if not resolvable (box renders as an editable input). */
export function fillValueForKey(
  key: string,
  patient: Patient | null | undefined,
  providerName?: string | null,
): string {
  if (key === 'provider.name') return providerName?.trim() ?? '';
  // Signing date: today (the BE re-fills this server-authoritatively at sign time).
  // ymdLocal, not toISOString().slice(0,10) — the latter converts to UTC first, so a
  // provider opening the popup between 00:00 and 04:00 Asia/Dubai saw YESTERDAY's date
  // on a consent form they were about to sign (PROD-1748).
  if (key === 'signing.date') return todayLocalYmd();
  if (!patient) return '';
  switch (key) {
    case 'patient.name':
      return joinName(patient.firstName, patient.middleName, patient.lastName);
    case 'patient.birthDate':
      return patient.birthDate ? patient.birthDate.slice(0, 10) : '';
    case 'patient.emiratesId':
      return patient.identification?.idNumber ?? '';
    case 'patient.mrn':
      return patient.patientMrn || patient.externalMRN || '';
    case 'patient.gender':
      return patient.genderName?.name ?? '';
    case 'patient.email':
      return patient.updatePatientContactRequest?.email || patient.email || '';
    case 'patient.phone':
      return patient.updatePatientContactRequest?.primaryContact || patient.phoneNumber || '';
    case 'patient.address':
      return defaultAddress(patient);
    case 'guardian.name': {
      const g = patient.guardianIdenitification;
      const gn = joinName(g?.firstName, g?.middleName, g?.lastName);
      if (gn) return gn;
      const ec = patient.emergencyContactResponse?.find(Boolean);
      return joinName(ec?.firstName, ec?.middleName, ec?.lastName);
    }
    case 'guardian.relation': {
      const g = patient.guardianIdenitification;
      if (g?.relationShip?.name) return g.relationShip.name;
      const ec = patient.emergencyContactResponse?.find(Boolean);
      return ec?.relation?.name ?? ec?.relationship ?? '';
    }
    default:
      return '';
  }
}

/** The guardian on file for a patient: the id the server matches a guardian signer against, and the name. */
export interface GuardianOnFile {
  id: string;
  name: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * PROD-2683. The guardian on file, or null when there is none.
 *
 * `guardianIdenitification.id` is `PatientGuardianRelation.GuardianId` (Scrips.Patient
 * `PatientService` read side). That is the value `AmbientConsentService` compares a guardian
 * signer's `SignerReferenceId` against, so a guardian signature without it is refused
 * (`guardian-signer-not-on-file`).
 *
 * Deliberately NOT the emergency contact that `guardian.name` falls back to for display: an
 * emergency contact is not a guardian on file, and signing as one is exactly what the server refuses.
 */
export function guardianOnFile(patient: Patient | null | undefined): GuardianOnFile | null {
  const g = patient?.guardianIdenitification;
  const id = g?.id?.trim();
  if (!id || id === EMPTY_GUID) return null;
  return { id, name: joinName(g?.firstName, g?.middleName, g?.lastName) || 'Guardian' };
}

/**
 * PROD-2683. The signer rows for a sign request. A guardian signer carries the guardian on file's
 * id as `signerReferenceId` and their real name. With no guardian on file there is no guardian
 * signer at all: the popup keeps the guardian box inert, and this refuses a stray capture too.
 */
export function buildConsentSigners(input: {
  patientSig: string | null;
  guardianSig: string | null;
  patientName?: string | null;
  guardian: GuardianOnFile | null;
  capabilityCode: string;
}): ConsentSigner[] {
  const signers: ConsentSigner[] = [];
  if (input.patientSig) {
    signers.push({
      signerRole: 'patient',
      signerName: input.patientName ?? 'Patient',
      signaturePng: input.patientSig,
      capabilityCode: input.capabilityCode,
    });
  }
  if (input.guardianSig && input.guardian) {
    signers.push({
      signerRole: 'guardian',
      signerReferenceId: input.guardian.id,
      signerName: input.guardian.name,
      signaturePng: input.guardianSig,
      capabilityCode: input.capabilityCode,
    });
  }
  return signers;
}

/**
 * Max length for a signature/stamp data URL — mirrors the BE bound
 * (`SignConsentRequest.MaxSignatureDataUrlLength`, Scrips.Patient #884). Pre-checking here
 * avoids POSTing an oversized body the BE would reject with a 400.
 */
export const MAX_SIGNATURE_DATA_URL_LENGTH = 4_000_000;

/**
 * Fetch an image URL (blob SAS) and return a base64 data URL, for the provider signature +
 * clinic stamp the FE sends on sign ("by who's using it"). Returns null on any failure
 * (CORS / 404 / not configured / over the size cap) — the BE then leaves that box blank
 * (non-fatal). The size cap matches the BE so an oversized asset never reaches the wire.
 */
export async function imageUrlToDataUrl(url: string | null | undefined): Promise<string | null> {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
    if (dataUrl && dataUrl.length > MAX_SIGNATURE_DATA_URL_LENGTH) return null;
    return dataUrl;
  } catch {
    return null;
  }
}
